/** @format */

import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSignalsInfo } from "../../redux/new-dashboard/dashboardSlice";
import { promptNoticeAccess, queueBrowserNotice } from "../../utility/helper";
import { useLocation } from "react-router-dom";

function dashboardDetail(signal) {
  const target = Number(signal?.tp1_percent);
  const stop = Number(signal?.sl_percent);
  const parts = [];
  if (Number.isFinite(target)) parts.push(`first target ${target.toFixed(2)}%`);
  if (Number.isFinite(stop)) parts.push(`stop ${stop.toFixed(2)}%`);
  return parts.length ? parts.join(" · ") : "New dashboard signal";
}

function useSignalAutoRefresh(interval = 5) {
  const {
    main: { userData },
  } = useSelector((state) => state);

  const signals = useSelector(
    (state) => state.dashboard.signalInfo.signals || [],
  );

  const dispatch = useDispatch();
  const location = useLocation();

  const isMountedRef = useRef(true);
  const seenSignalKeysRef = useRef(new Set());
  const primedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    promptNoticeAccess({ pathname: location.pathname });
    return () => {
      isMountedRef.current = false;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!userData?.email || !signals?.length) return;

    const fresh = [];
    signals.forEach((signal) => {
      const coin = String(signal?.symbol || "")
        .split("/")[0]
        .toUpperCase();
      const side = String(signal?.trade_type || "");
      const key = `${coin}|${side}|${signal?.signal_date || ""}`;
      if (!coin || !side || seenSignalKeysRef.current.has(key)) return;
      seenSignalKeysRef.current.add(key);
      if (primedRef.current) fresh.push({ signal, coin, side });
    });

    if (!primedRef.current) {
      primedRef.current = true;
      return;
    }
    if (!fresh.length || !isMountedRef.current) return;

    const one = fresh.length === 1;
    const title = one
      ? `${fresh[0].coin} ${fresh[0].side.toLowerCase()} signal just came in`
      : `${fresh.length} new signals came in`;
    const body = one
      ? dashboardDetail(fresh[0].signal)
      : fresh
          .slice(0, 2)
          .map((item) => `${item.coin} ${item.side.toLowerCase()}`)
          .join(" · ");
    queueBrowserNotice(title, { body });
  }, [signals, userData?.email]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!userData?.email) return;

    const fetchData = () => {
      dispatch(
        getSignalsInfo(userData["custom:custom_username"], userData.email),
      );
    };

    const timer = setInterval(fetchData, interval * 60 * 1000);

    return () => {
      isMountedRef.current = false;
      clearInterval(timer);
    };
  }, [
    userData?.email,
    userData?.["custom:custom_username"],
    dispatch,
    interval,
  ]);

  return null;
}

export default useSignalAutoRefresh;
